import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";

const SIZE_CONFIG = {
  small: {
    containerHeight: "36px",
    containerPadding: "3px",
    itemHeight: "30px",
    itemPadding: "0 12px",
    fontSize: "12px",
    borderRadius: "10px",
    itemRadius: "6px",
  },
  medium: {
    containerHeight: "48px",
    containerPadding: "4px",
    itemHeight: "40px",
    itemPadding: "0 18px",
    fontSize: "16px",
    borderRadius: "12px",
    itemRadius: "8px",
  },
  large: {
    containerHeight: "56px",
    containerPadding: "5px",
    itemHeight: "46px",
    itemPadding: "0 24px",
    fontSize: "18px",
    borderRadius: "14px",
    itemRadius: "10px",
  },
};

const DEFAULT_OPTIONS = ["1 Day", "1 Week", "1 Month", "3 Months"];

/**
 * TimeRangeSelector
 *
 * Props:
 *   value      {string}            – Currently selected option (controlled).
 *   onChange   {(value) => void}   – Called with the new value when user clicks an option.
 *   options    {string[]}          – List of option labels. Defaults to ["1 Day","1 Week","1 Month","3 Months"].
 *   size       {"small"|"medium"|"large"} – Visual size preset. Defaults to "medium".
 *   disabled   {boolean}           – Disables all interaction. Defaults to false.
 */
function TimeRangeSelector({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  size = "medium",
  disabled = false,
}) {
  const classes = useStyles();
  const cfg = SIZE_CONFIG[size] ?? SIZE_CONFIG.medium;

  return (
    <div
      className={clsx(classes.container, { [classes.containerDisabled]: disabled })}
      style={{ height: cfg.containerHeight, padding: cfg.containerPadding, borderRadius: cfg.borderRadius }}
    >
      {options.map((option) => {
        const isActive = option === value;
        return (
          <button
            key={option}
            disabled={disabled}
            className={clsx(classes.item, {
              [classes.itemActive]: isActive && !disabled,
              [classes.itemDisabled]: disabled,
            })}
            style={{
              height: cfg.itemHeight,
              padding: cfg.itemPadding,
              fontSize: cfg.fontSize,
              borderRadius: cfg.itemRadius,
            }}
            onClick={() => !disabled && onChange(option)}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

export default TimeRangeSelector;

const useStyles = makeStyles(() => ({
  container: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    boxSizing: "border-box",
  },
  containerDisabled: {
    opacity: 0.5,
  },
  item: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "Lato, sans-serif",
    fontWeight: 'normal',
    color: "#0C0E29",
    whiteSpace: "nowrap",
    transition: "background-color 0.15s ease",

    "&:hover:not(:disabled)": {
      backgroundColor: "#F5F5F5",
    },
  },
  itemActive: {
    backgroundColor: "#EDEDED",
    fontWeight: 'normal',
    cursor: "default",

    "&:hover": {
        backgroundColor: "#EDEDED",
    },
  },
  itemDisabled: {
    cursor: "not-allowed",
  },
}));
